import React from 'react'
import { RemoveScroll } from 'react-remove-scroll'
import { use100vh } from 'react-div-100vh'
import styled from '@emotion/styled'

import { Modal, ModalProps } from '../Private/Modal/Modal'
import { Box } from '../Box/Box'
import { Icon } from '../Icon/Icon'
import { SpaceProp } from '../Box/Box.types'
import { useScreenSize } from '../../hooks/useScreenSize'
import useResponsiveProp from '../../hooks/useResponsiveProp'
import { Heading } from '../Heading/Heading'
import { Divider } from '../Divider/Divider'

export interface DrawerProps extends Omit<ModalProps, 'position'> {
  inset?: ResponsiveProp<SpaceProp>
  headerInset?: ResponsiveProp<SpaceProp>
  width?: ResponsiveProp<'full' | 'content'>
  title?: string
  position?: 'left' | 'right'
  defaultClose?: boolean
}

export const Drawer = ({
  children,
  inset = 's',
  headerInset = 'none',
  width = 'content',
  position = 'right',
  defaultClose = true,
  title,
  onClose,
  ...restProps
}: DrawerProps) => {
  const closeDrawer = () => {
    onClose()
  }

  const device = useScreenSize()
  inset = useResponsiveProp(inset, device)
  headerInset = useResponsiveProp(headerInset, device)
  width = useResponsiveProp(width, device)

  const spaceMap = {
    xxs: 4,
    xs: 8,
    s: 12,
    m: 16,
    l: 24,
    xl: 32,
    xxl: 64,
    none: 0
  }
  const viewPortHeight = use100vh()
  const headerHeight = 24
  const totalHeaderHeight = headerHeight + spaceMap[inset as keyof typeof spaceMap]
  const bodyHeight = `calc(100% - ${totalHeaderHeight}px)`

  return (
    <Modal onClose={onClose} position={position} {...restProps}>
      <RemoveScroll forwardProps>
        <DrawerWrapper
          className="drawer-wrapper"
          background="var(--card--background)"
          height={viewPortHeight ?? undefined}
          width={width === 'full' ? '100%' : undefined}
          pointerEvents="all"
          tabIndex={0}
          inset={inset}
          position="relative"
        >
          {defaultClose || title ? (
            <>
              <Box
                width="100%"
                marginBottom={inset}
                display="flex"
                justifyContent={title ? 'between' : 'end'}
                minHeight={headerHeight}
                inset={headerInset}
              >
                {title && <Heading level={1}>{title}</Heading>}
                {defaultClose && (
                  <Box component="button" onClick={closeDrawer}>
                    <Icon name="close" tone="light" />
                  </Box>
                )}
              </Box>
              <Divider />
            </>
          ) : null}
          <Box
            height={defaultClose || title ? bodyHeight : '100%'}
            overflow="auto"
            paddingTop={defaultClose || title ? inset : 'none'}
          >
            {children}
          </Box>
        </DrawerWrapper>
      </RemoveScroll>
    </Modal>
  )
}

const DrawerWrapper = styled(Box)`
  &:focus {
    outline: 0;
  }
`
